import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Save, Loader2, Mail, Phone, User, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getImageUrl } from '../api';

export default function ProfileModal({ isOpen, onClose, user, onUpdate }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
      setPhotoPreview(getImageUrl(user.profile_photo_path) || null);
      setPhotoFile(null);
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handlePhotoClick = () => {
    fileInputRef.current.click();
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email);
      if (phone) formData.append('phone', phone);
      if (photoFile) formData.append('profile_photo', photoFile);

      const response = await api.post('/user/update-profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update local storage user data
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify({ ...user, ...updatedUser }));
      
      onUpdate(updatedUser);
      toast.success('Profile updated successfully');
      onClose();
    } catch (err) {
      console.error("Error updating profile:", err);
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-5 pb-2">
          <h2 className="text-xl font-black text-slate-800 tracking-tight">My Profile</h2>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 pt-1 space-y-4">
          {/* Avatar Section */}
          <div className="flex flex-col items-center justify-center pt-1 pb-3">
            <div 
              onClick={handlePhotoClick}
              className="relative w-20 h-20 rounded-full bg-slate-100 border-4 border-white shadow-lg overflow-hidden cursor-pointer group"
            >
              <img 
                src={photoPreview || `https://i.pravatar.cc/150?u=${user?.id}`} 
                alt="Profile" 
                className="w-full h-full object-cover group-hover:opacity-75 transition-opacity"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="text-white" size={24} />
              </div>
            </div>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept="image/*"
              onChange={handlePhotoChange}
            />
            <p className="text-[12px] font-semibold text-slate-500 mt-3 cursor-pointer hover:text-emerald-600 transition-colors" onClick={handlePhotoClick}>
              Click to upload new photo
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-[12px] font-bold text-slate-600">Full Name</label>
            <input 
              type="text" 
              required
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors shadow-sm"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[12px] font-bold text-slate-600">Username</label>
            <input 
              type="text" 
              readOnly
              disabled
              value={email}
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] font-semibold text-slate-500 bg-slate-50 cursor-not-allowed select-none shadow-sm"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[12px] font-bold text-slate-600">Phone Number</label>
            <input 
              type="tel" 
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors shadow-sm"
              placeholder="+91"
            />
          </div>

          <div className="pt-1">
            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-[#059669] hover:bg-[#047857] text-white font-bold text-[13px] py-2.5 rounded-md shadow-sm transition-colors flex items-center justify-center disabled:opacity-70"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : 'Save Changes'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
